// bm-template-internals.ts
import { withCellStyles, getCell, formats } from "../../utils";
import { Row, Rows } from "../../interfaces";
import {
  columnIndexToExcelLetter,
  generateSumFormulaForRow,
  generateSumFormulaForRowForYears,
} from "../../utils/functions";
import { StartAndEndLetter } from "./sections/management-fees-section";
import { backgroundGray, boldAndBackgroundGray, textBold } from "../../excelStyles";
import { BmItemValue } from "../../../models/budget-model/pojo/bm-classes-values";
import { BmPeriodEnum } from "../../../models/budget-model/pojo/bm-classes";
import { BmCalcType, BmDataFillingType } from "../../../models/budget-model/pojo/bm-enums";
import { BudgetModelFormGroup } from "../../../models/budget-model/pojo/bm-form";

export class BmTemplateInternals {
  private readonly TOTAL_COMMITMENT_REFERENCE = 'B2';
  private readonly NUMBER_OF_QUARTERS = 4;

  /** Builds a subgroup row when the data filling type is YEARLY; quarters are auto-distributed. */
  buildOperatingSubGroupRowForAnnualPeriodType(separatedArr: any[][], yearStandardValue: number, operatingSubGroupRow: Row, rowIndex: number): Row {
    let counter = 1;
    separatedArr.forEach((operatingItems) => {

      const genValue = this.calculateAdjustedValue(operatingItems as BmItemValue[], yearStandardValue);
      operatingItems.forEach((operatingItem) => {

        if (operatingItem === null) {
          operatingSubGroupRow.push(null);
          counter += 1;
        } else if (operatingItem.period.type === BmPeriodEnum.YEAR) {
          const columnLetterStart = columnIndexToExcelLetter(counter - this.NUMBER_OF_QUARTERS);
          const columnLetterEnd = columnIndexToExcelLetter(counter - 1);
          operatingSubGroupRow.push(withCellStyles(getCell({
            f: generateSumFormulaForRow(columnLetterStart, columnLetterEnd, rowIndex),
            z: formats.thousandsSeparator,
            t: "n",
          }), backgroundGray)
          );
          counter += 1;
        } else if (operatingItem.period.type === BmPeriodEnum.QUARTER) {
          counter += 1;
          const cellValue = operatingItem.calcType === BmCalcType.MANUAL ? operatingItem.itemValue : genValue;
          operatingSubGroupRow.push(withCellStyles(getCell({
            v: cellValue,
            z: formats.thousandsSeparator,
            t: "n",
          }), null)
          );
        }
      });
    });
    return operatingSubGroupRow;
  }

  /** Inserts a null placeholder after each yearly (Y*) header.
   * After every year value needs to add small empty column
   * null acts as a marker narrow empty column in the sheet
   */
  insertNullValuesInToHeadersAfterYear(headers: string[]): string[] {
    const updated: string[] = [];
    for (const h of headers) {
      updated.push(h);
      if (h.startsWith("Y")) {
        updated.push(null);
      }
    }
    return updated;
  }

  /** Inserts null after each YEAR item in the data row to align with header layout. */
  setNullToDataAfterYearForBmItems(initialRow: BmItemValue[], startsWithNull = true): BmItemValue[] {
    const updated: BmItemValue[] = startsWithNull ? ([null] as BmItemValue[]) : [];
    initialRow.forEach((item) => {
      if (item.period.type === BmPeriodEnum.YEAR) {
        updated.push(item);
        updated.push(null);
      } else {
        updated.push(item);
      }
    });
    return updated;
  }

  /** Appends three gray empty cells for the Total columns block. */
  setEmptyTotalsRowWithStyle(row: Row): void {
    const count = 3;
    row.push(...Array.from({ length: count }, () => withCellStyles("", backgroundGray)));
  }

  /**
   * Splits an array into year-sized chunks (5 items) and appends a null after each chunk.
   * Intended for arranging Q1..Q4 + Y blocks.
   */
  separateArrByYear<T>(arr: T[]): Array<T[]> {
    const chunkSize = this.NUMBER_OF_QUARTERS + 1;
    const result: Array<T[]> = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
      const chunk = arr.slice(i, i + chunkSize);
      (chunk).push(null);
      result.push(chunk);
    }
    return result;
  }

  /** Produces an empty row styled to shade Y and Total columns. */
  prepareEmptyRowWithDesign(headers: string[]): Rows {
    const mainRow: Rows = [];
    const row: any[] = [];
    headers.forEach((h) => {
      row.push(withCellStyles("", (this.doesHeaderIncludeYear(h) || this.doesHeaderIncludeTotal(h)) ? backgroundGray : null));
    });
    mainRow.push(row);
    return mainRow;
  }

  /**
   * Normalizes a numeric value to a quarterly amount based on the filling type:
   * - YEARLY => value / 4
   * - QUARTERLY => value
   * - MONTHLY => value * 3
   */
  defineTypeOfCountingExpenses(value: number, type: BmDataFillingType): number {
    switch (type) {
      case BmDataFillingType.YEARLY: {
        return value / this.NUMBER_OF_QUARTERS;
      }
      case BmDataFillingType.QUARTERLY: {
        return value;
      }
      case BmDataFillingType.MONTHLY: {
        return value * 3;
      }
      default: {
        return value;
      }
    }
  }

  /** Initializes current template state from the form and headers. */
  defineCurrent(budgetForm: BudgetModelFormGroup, headers: string[]): BudgetModalTemplateCurrentState {
    const current = this.generateCurrentInitialState();
    current.capitalRequiredTotalRow = budgetForm.controls.modelData.controls.totalCapRequired.value.totalRow;
    current.investmentStandardValue = budgetForm.controls.modelData.controls.investment.value.investmentValue;
    current.totalCommitment = budgetForm.value.previousTotalCommitment;
    current.indexOfTotalColumn = headers.indexOf("Total");
    current.yearsIndexArray = this.defineYearsIndexes(headers);
    current.headersForLogic = headers;
    current.headersWithOutTotalTail = headers.slice(0, -3);
    current.expandedHeadersWithYear = this.expandQuarterHeadersWithYear(current.headersWithOutTotalTail);
    return current;
  }

  pushYearsTotalCellBold(row: Row, rowIndex: number, yearsIndexArray: number[]): void {
    row.push(withCellStyles(getCell({
      f: `${generateSumFormulaForRowForYears(yearsIndexArray, rowIndex)}`,
      z: formats.thousandsSeparator, t: 'n',
    }), boldAndBackgroundGray));
  }

  pushYearsTotalCellGray(row: Row, rowIndex: number, yearsIndexArray: number[]): void {
    row.push(withCellStyles(getCell({
      f: `${generateSumFormulaForRowForYears(yearsIndexArray, rowIndex)}`,
      z: formats.thousandsSeparator, t: 'n',
    }), backgroundGray));
  }

  pushSumOrValueWIthOutZeroIndex(row: Row, index: number, value: BmItemValue, rowIndex: number, isTotal = false): void {
    if (!value) {
      row.push(null);
      return;
    }
    if (value.period.type === BmPeriodEnum.YEAR) {
      const start = this.startAndEndLettersForYearSum(false, index).start;
      const end = this.startAndEndLettersForYearSum(false, index).end;
      row.push(withCellStyles(getCell({
        f: generateSumFormulaForRow(start, end, rowIndex),
        z: formats.thousandsSeparator, t: 'n',
      }), isTotal ? boldAndBackgroundGray : backgroundGray));
    } else {
      row.push(withCellStyles(getCell({
        v: value.itemValue,
        z: formats.thousandsSeparator, t: 'n',
      }), isTotal ? textBold : null));
    }
  }

  pushSumOrValueWithZeroIndex(row: Row, index: number, value: BmItemValue, rowIndex: number): void {
    if (!value) {
      row.push(null);
      return;
    }
    if (value.period.type === BmPeriodEnum.YEAR) {
      const start = this.startAndEndLettersForYearSum(true, index).start;
      const end = this.startAndEndLettersForYearSum(true, index).end;
      row.push(withCellStyles(getCell({
        f: generateSumFormulaForRow(start, end, rowIndex),
        z: formats.thousandsSeparator, t: 'n',
      }), boldAndBackgroundGray));
    } else {
      row.push(withCellStyles(getCell({
        v: value.itemValue,
        z: formats.thousandsSeparator, t: 'n',
      }), textBold));
    }
  }

  prepareCellForTotalPcnt(row: Row, current: BudgetModalTemplateCurrentState, boldText = false): void {
    row.push(withCellStyles(getCell({
      f: `IFERROR(${columnIndexToExcelLetter(current.indexOfTotalColumn)}${current.rowIndex}/${current.capitalRequiredTotalRow},0)`,
      z: formats.percents, t: 'n',
    }), boldText ? boldAndBackgroundGray : backgroundGray));
  }

  setCellForTotalPcntOfCommitment(row: Row, rowIndex: number, indexOfTotalColumn: number, boldText = false): void {
    row.push(withCellStyles(getCell({
      f: `IFERROR(${columnIndexToExcelLetter(indexOfTotalColumn)}${rowIndex}/${this.TOTAL_COMMITMENT_REFERENCE},0)`,
      z: formats.percents, t: 'n',
    }), boldText ? boldAndBackgroundGray : backgroundGray));
  }

  /**
 * Computes the auto value for non-manual quarters given a yearly standard value and manual overrides.
 * - If no manual quarters exist, returns standardValue / 4.
 * - Otherwise distributes the remaining amount evenly across non-manual quarters.
 */
  private calculateAdjustedValue(items: BmItemValue[], standardValue: number): number {
    const quarters = items.filter((item) => item && item.period.type === BmPeriodEnum.QUARTER);
    const manualItems = quarters.filter((item) => item.calcType === BmCalcType.MANUAL);

    if (manualItems.length === 0) {
      return standardValue / this.NUMBER_OF_QUARTERS;
    }

    const manualSum = manualItems.reduce((sum, item) => sum + (item.itemValue ?? 0), 0);
    const remainingCount = this.NUMBER_OF_QUARTERS - manualItems.length;
    if (remainingCount <= 0) {
      return 0;
    }
    return (standardValue - manualSum) / remainingCount;
  }

  /** Expands headers so that Q headers are paired with the following Y header (e.g., q1(y1)).
   *  Used for Excel formula generation. Every value in the expanded headers must be unique.
   * example: ['q1','q2','q3','q4','y1', null] → ['q1(y1)','q2(y1)','q3(y1)','q4(y1)','y1', null]
   */
  private expandQuarterHeadersWithYear(originalHeaders: string[]): string[] {
    const preparedHeaders: string[] = [];
    let pendingQuarters: string[] = [];
    for (const item of originalHeaders) {
      if (item !== null && item.includes('Y')) {
        preparedHeaders.push(...pendingQuarters.map((q) => `${q}(${item.toLowerCase()})`));
        pendingQuarters = [];
        preparedHeaders.push(item);
      } else if (item !== null && /^q\d+$/i.test(item)) {
        pendingQuarters.push(item.toLowerCase());
      } else {
        preparedHeaders.push(item);
      }
    }
    return preparedHeaders;
  }

  /**
 * Builds an Excel SUM formula for a 4-slot "year" window using an array-based column index.
   * indexMayBeZero === true → include current index: [index-3 .. index]
   * indexMayBeZero === false → exclude current: [index-4 .. index-1]
   */
  private startAndEndLettersForYearSum(indexMayBeZero: boolean, index: number): StartAndEndLetter {
    const startIndex = indexMayBeZero ? index - 3 : index - 4;
    const endIndex = indexMayBeZero ? index : index - 1;
    return {
      start: columnIndexToExcelLetter(startIndex),
      end: columnIndexToExcelLetter(endIndex),
    };
  }

  /** Collects indices of 'Y*' headers. */
  private defineYearsIndexes(headers: string[]): number[] {
    const yearIndexes: number[] = [];
    headers.forEach((h, i) => {
      if (h && h.includes("Y")) {
        yearIndexes.push(i);
      }
    });
    return yearIndexes;
  }

  private doesHeaderIncludeYear(header: string): boolean {
    return Boolean(header) && header.includes("Y");
  }

  private doesHeaderIncludeTotal(header: string): boolean {
    return Boolean(header) && (header.includes('Total') || header.includes("%"));
  }

  /** Returns a default state for the template.
   * Rows 1–4 are reserved for the Total Commitment value, a spacer row, and headers;
   * data insertion starts at row 5.
   */
  private generateCurrentInitialState(): BudgetModalTemplateCurrentState {
    return {
      rowIndex: 5,
      totalCapitalReqIndex: [],
      indexArrayForTotalExpenses: [],
      totalCommitment: 0,
      yearsIndexArray: [],
      capitalRequiredTotalRow: 0,
      indexOfTotalColumn: 0,
      investmentStandardValue: 0,
      expandedHeadersWithYear: [],
      headersWithOutTotalTail: [],
      headersForLogic: [],
      hasCatchUp: false,
    };
  }
}

/** Shared template state used across sections while building rows. */
export interface BudgetModalTemplateCurrentState {
  rowIndex: number;
  totalCapitalReqIndex: number[];
  indexArrayForTotalExpenses: number[];
  totalCommitment: number;
  yearsIndexArray: number[];
  capitalRequiredTotalRow: number;
  indexOfTotalColumn: number;
  investmentStandardValue: number;
  expandedHeadersWithYear: string[];
  headersWithOutTotalTail: string[];
  headersForLogic: string[];
  hasCatchUp: boolean;
}

